import EventEmitter from 'event-emitter';
import MD5 from './MD5';
import { isFile } from '../utils/utils';
import log from 'loglevel';
const FORMATS = { rmvb: 0, mp4: 0, avi: 0, wmv: 0, mkv: 0, flv: 0, mov: 0, mp3: 0, wav: 0, m4a: 0, aac: 0, srt: 1 };
let id = 1;

export default class Task {
  constructor(api) {
    EventEmitter(this);
    this._api = api;
    this.id = id++;
    // https://docs.microsoft.com/zh-cn/graph/api/driveitem-createuploadsession?view=graph-rest-1.0#best-practices
    this._chunkSize = 1024 * 1024 * 2;
    this._hash = null; // hash 计算程序
    this._pauseOne = false;
    this._pausea = false;
    this._process = null; // 上传处理程序
    this._stage = 0; // 0-待上传 1-hash 2-checkFile 3-upload 4-uploadStep 5-done

    this.status = 0; // 0-待上传 1-成功 2-待上传 3-失败
    this.quickUpload = false; // 是否快传
    this.isPause = false; // 是否暂停中

    this.error = null; // 上传报错
    this.offset = 0; // 文件上传位置offset
    this.progress = 0; // 上传进度 0～1
    this.size = 0; // 文件大小
    this.md5 = null; // md5
    this.filename = null; // 文件名
    this.file = null; // 文件
    this.fileType = null; // 文件类型 0视频 1字幕
    this.result = null; // 上传结果
  }

  init(params) {
    const { file, size, filename, fileType } = this.check(params);
    this.file = file;
    this.size = size;
    this.filename = filename;
    this.fileType = fileType;
    this._hash = new MD5();
  }

  check(params) {
    const file = params && params.file;
    if (!isFile(file)) throw new Error('file参数格式错误');
    const size = file.size;
    const filename = file.name || '';
    const fileExt = filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();
    const fileType = FORMATS[fileExt];

    if (!size) throw new Error('不允许空文件');
    if (typeof fileType === 'undefined') throw new Error('文件类型错误，不支持的文件类型');

    return { file, size, filename, fileExt, fileType };
  }

  pause() {
    throw new Error('不要直接调用任务的暂停');
  }
  resume() {
    throw new Error('不要直接调用任务的开始');
  }
  destroy() {
    throw new Error('不要直接调用任务的销毁');
  }

  _pause() {
    log.info('_pause()', this.filename);
    this._setPause(true);
    if (this._stage === 1 && this._hash) this._hash.pauseSparkMD5();
    if (!this._pausea) this._pauseOne = true;
  }
  _resume() {
    log.info('_resume()', this.filename);
    this._setPause(false);
    if (this._stage === 1 && this._hash && this._hash.pause) this._hash.resumeSparkMD5();

    if (!this._process) {
      this._process = this._start();
      this._next();
    }
    this.emit('continue', this);
  }

  _setPause(v) {
    this._pausea = !!v;
    this.isPause = !!v;
  }

  async _next() {
    // 已完成
    if (this._stage === 5) return;
    let done = false;
    let result;
    do {
      try {
        result = await this._process.next();
      } catch (e) {
        this.status = 3;
        this.error = e;
        this.emit('error', this, e);
        return;
      }

      done = result.done;
      this._stage = result.value;
      // 更新进度
      const progress = Math.floor((this.offset / this.file.size) * 1000) / 1000;
      // 如果暂停了，则不派发进度
      if (!this._pausea && !this._pauseOne && progress !== 0 && progress !== 1) {
        this.progress = progress;
        this.emit('progress', this);
      }
      if (this._pausea || this._pauseOne) {
        this.emit('pause', this);
        this._pauseOne = false;
        await new Promise(resolve =>
          this.once('continue', () => {
            this.emit('resume', this);
            resolve();
          })
        );
      }
    } while (!done);

    this.progress = 1;
    this.status = 1;
    // 已完成
    this.emit('progress', this);
    this.emit('done', this);
  }

  async *_start() {
    log.info('*_start()', this.filename);

    // step 0
    const file_name = this.file.name;
    const file_size = this.file.size;
    const file_type = this.fileType || 0;

    // stage 1
    // 计算hash
    yield 1;
    await this._hash.setFile(this.file);
    if (!this.md5) {
      if (process.env.NODE_ENV !== 'production') console.time('hash' + this.filename);
      await new Promise((resolve, reject) => {
        this._hash.once('md5', info => {
          this.md5 = info.md5;
          this.file.md5 = info.md5;
          resolve(info);
        });
        this._hash.once('error', function (error) {
          const err = new Error('计算文件hash出错');
          err.origin = error;
          reject(err);
        });
        if (this._hash && this._hash.pause) this._hash.resumeSparkMD5();
      });
      this._hash.destroy();
      this._hash = null;
      if (process.env.NODE_ENV !== 'production') console.timeEnd('hash' + this.filename);
      log.info('hash:', this.md5, this.file.name);
    }

    // stage 2
    // 检查上传进度
    yield 2;
    const { file_offset, file_path, status } = await this._api.checkFile({
      file_name,
      file_type,
      file_size,
      md5: this.md5
    });
    if (status === 2) {
      yield 5;
      this.offset = this.size;
      this.quickUpload = true;
      this.result = { filePath: file_path };
      return true;
    } else if (status === 1) {
      this.offset = file_offset;
    }

    // stage 3
    yield 3;
    let end = this.offset;
    // 重试
    let retry = 3;
    let err = null;

    // 循环上传文件分片
    do {
      var _start = end;
      var _end = Math.min(this.file.size, _start + this._chunkSize);
      const isLastPart = _end - _start < this._chunkSize;

      // 分片上传
      if (!isLastPart) {
        if (err) retry -= 1;
        err = null;
        try {
          await this._uploadPart(this.file, _start, _end, this.md5, file_type);
        } catch (e) {
          err = e;
        }

        if (err) {
          // 重试次数用完且有错误
          if (!retry) throw err;
        } else {
          end = _end;
          this.offset = _end;
          retry = 3;
        }
        yield 3;
      } else {
        // stage 4
        // 最后一片文件
        // 出错之后不要进行重试，并检查一次文件是不是已经传输成功了
        // 同时两个窗口上传可能出现这种情况
        yield 4;
        try {
          const { file_path } = await this._uploadLastPart(this.file, _start, _end, this.md5, file_type, isLastPart);
          this.result = { filePath: file_path };
        } catch (e) {
          // 大文件最后处理会很慢，等一会再检查
          if (this.size > 1024 * 1024 * 1024) {
            await new Promise(resolve => setTimeout(resolve, 1000 * 6));
          }
          const { file_offset, file_path, status } = await this._api.checkFile({
            file_name,
            file_type,
            file_size,
            md5: this.md5
          });
          if (status === 2) {
            yield 5;
            this.offset = this.size;
            this.result = { filePath: file_path };
            return true;
          } else if (status === 1) {
            this.offset = file_offset;
            throw e;
          }
        }
        // success
        yield 5;
        this.offset = this.size;
        return true;
      }
    } while (end < this.file.size);
  }

  _uploadLastPart(file, start, end, md5, fileType, lastPart) {
    return this._uploadPart(file, start, end, md5, fileType, lastPart);
  }

  async _uploadPart(file, start, end, md5, fileType, lastPart) {
    if (start >= file.size) return;
    const params = {
      file_offset: start,
      file_name: file.name,
      file_size: file.size,
      file: file.slice(start, end),
      file_type: fileType,
      md5
    };

    let timeout = lastPart ? 900 * 1000 : 120 * 1000;
    let res = await this._api.uploadFile(params, timeout);
    return res;
  }

  _destroy() {
    const hash = this._hash;
    const $process = this._process;
    const md5 = this.md5;
    const file = this.file;
    const status = this.status;
    this._pauseOne = true;
    this._pausea = true;
    this.isPause = true;
    this._hash = null;
    this._stage = 0;
    this.status = 0;

    this._process = null;
    this.offset = 0;
    this.progress = 0;
    this.size = 0;
    this.md5 = null;
    // this.file = null;

    // 清理中间文件
    if (md5 && file && status === 3) this.deleteFile(file.name, md5, this.fileType || 0);
    if ($process) $process.return();
    if (hash) hash.destroy();
  }

  fileUniqKey() {
    if (!this.file) return;
    return [this.file.lastModified, this.file.name, this.file.size].join('|');
  }

  async deleteFile(file_name, md5, file_type) {
    const params = { file_name, md5, file_type: file_type || this.fileType };
    const res = await this._api.deleteFile(params);
    return res;
  }
}
